import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import { createElement } from "react";
import type { FieldUiOptions } from "./types";

import { SectionNodeView } from "./views/section-node-view";

/**
 * SectionTitle node — inline-only content for entity names and labels.
 *
 * Used as the first field in every entity document schema.
 * Rendered with large title styling; the label attribute provides
 * a subtle heading above the editable area.
 *
 * Examples: KPI name, dataset name, objective label, question label.
 */
export const SectionTitle = Node.create({
  name: "sectionTitle",
  content: "inline*",
  defining: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {} as Record<string, unknown>,
      fieldUiOptions: {} as FieldUiOptions,
    };
  },

  addAttributes() {
    return {
      fieldId: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-field-id") ?? "",
        renderHTML: (attributes) => ({ "data-field-id": attributes.fieldId }),
      },
      label: {
        default: "Title",
        parseHTML: (element) => element.getAttribute("data-label") ?? "Title",
        renderHTML: (attributes) => ({ "data-label": attributes.label }),
      },
      guidance: { default: "", rendered: false },
      chromeless: { default: false, rendered: false },
      ownerId: { default: null, rendered: false },
      _diffStatus: { default: null, rendered: false },
      _diffOldValue: { default: null, rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="section-title"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "section-title",
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) =>
      createElement(SectionNodeView, {
        ...props,
        fieldUiOptions: this.options.fieldUiOptions,
      })
    );
  },
});
